import type { BacklogConfig } from "../contracts/BacklogConfig";
import type { BacklogApiContext } from "../contracts/api/BacklogApiContext";
import type { BacklogRequestBody } from "../contracts/api/BacklogRequestOption";
import type { BacklogResponse } from "../contracts/api/BacklogResponse";
import type { BacklogResponseFile } from "../contracts/api/BacklogResponseFile";
import type { BacklogResponseNoContent } from "../contracts/api/BacklogResponseNoContent";
import type { PostAttachmentFileParams } from "../endpoints/params/PostAttachmentFileParams";
import { BacklogError } from "../errors/BacklogError";
import {
	assert,
	createURLSearchParams,
	joinUrlPath,
	resolveBacklogContainer,
} from "../utils/BacklogUtil";

function v2path(path: string): string {
	return joinUrlPath("/api/v2/", path);
}

function replaceUrlPath<T extends object>(
	options: {
		path: string;
		params?: T;
	},
	exclude: string[],
): string {
	const path = options.path;
	const params = options.params;
	return path.replace(/:[a-z0-9_]+/gi, (matched) => {
		const key = matched.slice(1) as keyof T; // Remove the ':' prefix
		if (params) {
			if (key in params) {
				const val = (params[key] || "").toString();
				exclude.push(String(key));
				return encodeURIComponent(val);
			}
		}
		throw new BacklogError(`Param ${String(key)} not found: ${path}`);
	});
}

function createApiPath<T extends object>(
	options: {
		path: string;
		params?: T;
	},
	exclude: string[],
) {
	const path = options.params ? replaceUrlPath(options, exclude) : options.path;
	const api_path = v2path(path);
	return api_path;
}

function createApiParam<T extends object>(
	options: {
		path: string;
		params?: T;
	},
	exclude: string[],
) {
	const param = options.params
		? createURLSearchParams(options.params, exclude)
		: undefined;
	return param;
}

function createInternalOption(options: {
	path: string;
}) {
	const internal: {
		endpoint: string;
	} = {
		endpoint: v2path(options.path),
	};
	return internal;
}

export function requestApiGet<T extends object, U extends object>(options: {
	config: BacklogConfig;
	path: string;
	params: T | undefined;
	context: BacklogApiContext | undefined;
}): Promise<BacklogResponse<U>> {
	const request = resolveBacklogContainer("BacklogRequest", {
		config: options.config,
	});
	const exclude: string[] = [];
	const api_path = createApiPath(options, exclude);
	const param = createApiParam(options, exclude);
	return request.get<U>({
		path: api_path,
		query: param,
		context: options.context,
		internal: createInternalOption(options),
	});
}

export function requestApiGetFile<T extends object>(options: {
	config: BacklogConfig;
	path: string;
	params: T | undefined;
	context: BacklogApiContext | undefined;
}): Promise<BacklogResponse<BacklogResponseFile>> {
	assert(options);
	assert(options.config);
	assert(options.path);
	const request = resolveBacklogContainer("BacklogRequest", {
		config: options.config,
	});
	return request.getFile({
		path: createApiPath(options, []),
		context: options.context,
		internal: {
			endpoint: v2path(options.path),
		},
	});
}

export function requestApiPost<T extends object, U extends object>(options: {
	config: BacklogConfig;
	path: string;
	params: T | undefined;
	context: BacklogApiContext | undefined;
}): Promise<BacklogResponse<U>> {
	assert(options);
	assert(options.config);
	assert(options.path);

	const request = resolveBacklogContainer("BacklogRequest", {
		config: options.config,
	});
	const exclude: string[] = [];
	const api_path = createApiPath(options, exclude);
	const param = createApiParam(options, exclude);
	return request.post<U>({
		path: api_path,
		query: undefined,
		body: param,
		context: options.context,
		internal: createInternalOption(options),
	});
}

export function requestApiPostData<U extends object>(options: {
	config: BacklogConfig;
	path: string;
	params: BacklogRequestBody;
	context: BacklogApiContext | undefined;
}): Promise<BacklogResponse<U>> {
	assert(options);
	assert(options.config);
	assert(options.path);

	const request = resolveBacklogContainer("BacklogRequest", {
		config: options.config,
	});
	const api_path = createApiPath(
		{
			path: options.path,
		},
		[],
	);
	return request.post<U>({
		path: api_path,
		query: undefined,
		body: options.params,
		context: options.context,
		internal: createInternalOption(options),
	});
}

export function requestApiPostNoContent<T extends object>(options: {
	config: BacklogConfig;
	path: string;
	params: T | undefined;
	context: BacklogApiContext | undefined;
}): Promise<BacklogResponse<BacklogResponseNoContent>> {
	assert(options);
	assert(options.config);
	assert(options.path);

	const request = resolveBacklogContainer("BacklogRequest", {
		config: options.config,
	});
	const exclude: string[] = [];
	const api_path = createApiPath(options, exclude);
	const param = createApiParam(options, exclude);
	return request.postNoContent({
		path: api_path,
		query: undefined,
		body: param,
		context: options.context,
		internal: createInternalOption(options),
	});
}

export function requestApiPut<T extends object, U extends object>(options: {
	config: BacklogConfig;
	path: string;
	params: T | undefined;
	context: BacklogApiContext | undefined;
}): Promise<BacklogResponse<U>> {
	assert(options);
	assert(options.config);
	assert(options.path);

	const request = resolveBacklogContainer("BacklogRequest", {
		config: options.config,
	});
	const exclude: string[] = [];
	const api_path = createApiPath(options, exclude);
	const param = createApiParam(options, exclude);
	return request.put<U>({
		path: api_path,
		query: undefined,
		body: param,
		context: options.context,
		internal: createInternalOption(options),
	});
}

export function requestApiPatch<T extends object, U extends object>(options: {
	config: BacklogConfig;
	path: string;
	params: T | undefined;
	context: BacklogApiContext | undefined;
}): Promise<BacklogResponse<U>> {
	assert(options);
	assert(options.config);
	assert(options.path);

	const request = resolveBacklogContainer("BacklogRequest", {
		config: options.config,
	});
	const exclude: string[] = [];
	const api_path = createApiPath(options, exclude);
	const param = createApiParam(options, exclude);
	return request.patch<U>({
		path: api_path,
		query: undefined,
		body: param,
		context: options.context,
		internal: createInternalOption(options),
	});
}

export function requestApiDelete<T extends object, U extends object>(options: {
	config: BacklogConfig;
	path: string;
	params: T | undefined;
	context: BacklogApiContext | undefined;
}): Promise<BacklogResponse<U>> {
	assert(options);
	assert(options.config);
	assert(options.path);

	const request = resolveBacklogContainer("BacklogRequest", {
		config: options.config,
	});
	const exclude: string[] = [];
	const api_path = createApiPath(options, exclude);
	const param = createApiParam(options, exclude);
	return request.delete<U>({
		path: api_path,
		query: undefined,
		body: param,
		context: options.context,
		internal: createInternalOption(options),
	});
}

export function createUploadParam(options: {
	params: PostAttachmentFileParams;
}): {
	contents: Blob;
	filename?: string;
} {
	assert(options.params);
	assert(options.params.contents);
	let contents = options.params.contents;
	let filename = options.params.filename;

	if (contents instanceof File) {
		if (!filename) {
			filename = contents.name;
		}
	} else if (contents instanceof Blob) {
		// NOP
	} else {
		contents = new Blob([contents], { type: "text/plain" });
	}

	return {
		contents,
		filename,
	};
}
